import React, { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { billingView, formatBillingDate } from '../lib/billingStatus'
import { Bell, CreditCard, Globe, Info, LogOut } from 'lucide-react'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import { useLangPrefix } from '../hooks/useLangPrefix'
import useManagerStore from '../store/managerStore'
import { useAuthStore } from '../store/authStore'
import { APP_VERSION } from '../config'
import { getTenant } from '../api/tenant'
import { getCreditStatus } from '../api/billing'
import { getManagerUsers } from '../api/users'
import { updateProfile, changePassword, updateBusiness, uploadAvatar, deleteAvatar } from '../api/settings'
import { resizeImage } from '../lib/resizeImage'
import UserAvatar from '../components/common/UserAvatar'
import { registerPushToken, unregisterPushToken } from '../api/push'
import { requestPushPermission } from '../lib/pushNotifications'
import api from '../lib/api'
import PasswordInput from '../components/common/PasswordInput'
import { canAccess, normalizeRole, RBAC_MODULES } from '../lib/rbac'

const ROLE_STYLE = {
  admin: { bg: "bg-[#e8ecfe]", text: "text-[#1535c8]" },
  manager: { bg: "bg-[#e8ecfe]", text: "text-[#1535c8]" },
  supervisor: { bg: "bg-[#f3e5f5]", text: "text-[#6a1b9a]" },
  receptionist: { bg: "bg-[#e8f5e9]", text: "text-[#2e7d32]" },
};

const USER_STATUS_STYLE = {
  active: {
    bg: "bg-[#e8f5e9]",
    text: "text-[#2e7d32]",
    labelKey: "settingsPage.userStatus.active",
  },
  invited: {
    bg: "bg-[#fff8e1]",
    text: "text-[#f57f17]",
    labelKey: "settingsPage.userStatus.invited",
  },
  suspended: {
    bg: "bg-[#ffebee]",
    text: "text-[#c62828]",
    labelKey: "settingsPage.userStatus.suspended",
  },
};

const BUSINESS_TYPES = [
  { value: "fnb", label: "Food & Beverage" },
  { value: "hotel", label: "Hotel & Resort" },
  { value: "spa", label: "Spa & Wellness" },
  { value: "other", label: "Other" },
];

function SettingRow({ icon, label, right }) {
  return (
    <div className="px-4 py-3 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <span className="text-text-hint">{icon}</span>
        <span className="text-text-main text-sm font-medium">{label}</span>
      </div>
      <span className="text-text-hint text-sm">{right}</span>
    </div>
  );
}

const GlobeIcon = <Globe size={18} />
const InfoIcon = <Info size={18} />
const BillingIcon = <CreditCard size={18} />
const LogoutIcon = <LogOut size={18} />
const BellIcon = <Bell size={18} />

const FIELD_CLASS =
  "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-main outline-none placeholder:text-text-secondary focus:border-primary disabled:bg-page-bg disabled:text-text-muted";

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  disabled = false,
  autoComplete = "off",
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-text-muted">{label}</span>
      {type === "password" ? (
        <PasswordInput
          className={FIELD_CLASS}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
        />
      ) : (
        <input
          type={type}
          className={FIELD_CLASS}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
        />
      )}
    </label>
  );
}

function TextareaField({
  label,
  value,
  onChange,
  placeholder = "",
  rows = 3,
  disabled = false,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-text-muted">{label}</span>
      <textarea
        className={`${FIELD_CLASS} resize-none`}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
      />
    </label>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-2xl border border-border bg-white p-5 shadow-sm">
      {title && <h2 className="mb-4 text-sm font-semibold text-text-main">{title}</h2>}
      {children}
    </section>
  );
}

function SaveRow({ message, error, busy, onSave, label, busyLabel }) {
  return (
    <div className="mt-5 flex items-center justify-end gap-3">
      {message && <span role="status" className="text-sm text-green-600">{message}</span>}
      {error && <span role="alert" className="text-sm text-red-500">{error}</span>}
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50"
      >
        {busy ? busyLabel : label}
      </button>
    </div>
  );
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { prefix } = useLangPrefix();
  const {
    role: storeRole,
    tenantId,
    setCreditStatus,
    creditStatus,
  } = useManagerStore();
  const authUser = useAuthStore((s) => s.user);
  const orgId = useAuthStore((s) => s.orgId);
  const logoutAuth = useAuthStore((s) => s.logout);
  const setAuthUser = useAuthStore((s) => s.setUser);
  const fileInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const [tenant, setTenantLocal] = useState(null);
  const [users, setUsers] = useState([]);

  const [activeTab, setActiveTab] = useState("account");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState("");
  const [profileSaveSuccess, setProfileSaveSuccess] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessSaveError, setBusinessSaveError] = useState("");
  const [businessSaveSuccess, setBusinessSaveSuccess] = useState("");

  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const [bizName, setBizName] = useState("");
  const [bizType, setBizType] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [bizHours, setBizHours] = useState("");
  const [bizTimezone, setBizTimezone] = useState("");
  const [bizPersona, setBizPersona] = useState("");
  const [bizWelcome, setBizWelcome] = useState("");
  const normalizedRole = normalizeRole(storeRole);
  const canAccessBilling = canAccess(normalizedRole, RBAC_MODULES.billing);

  const [pushEnabled, setPushEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  )
  const [pushBusy, setPushBusy] = useState(false)
  const [pushToken, setPushToken] = useState(null)

  async function handleTogglePush() {
    if (pushBusy || !tenantId) return
    setPushBusy(true)
    try {
      if (pushEnabled) {
        // pushToken in state is only populated by the *enable* branch below
        // within this page's own lifetime — on a fresh mount (push already
        // enabled from a previous session) it's still null. Re-derive the
        // live token via requestPushPermission() (resolves instantly, no
        // prompt, when permission is already 'granted') instead of trusting
        // possibly-stale local state, so disabling actually unregisters the
        // device that's really registered.
        const liveToken = pushToken ?? (await requestPushPermission())
        if (liveToken) await unregisterPushToken(tenantId, liveToken)
        setPushToken(null)
        setPushEnabled(false)
      } else {
        const token = await requestPushPermission()
        if (token) {
          await registerPushToken(tenantId, token)
          setPushToken(token)
          setPushEnabled(true)
        }
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'toggle_push' }, extra: { tenantId } })
    } finally {
      setPushBusy(false)
    }
  }

  useEffect(() => {
    async function load() {
      if (!tenantId) return;
      try {
        const [t, cs] = await Promise.all([
          getTenant(tenantId),
          canAccessBilling ? getCreditStatus(tenantId) : Promise.resolve(null),
        ]);
        setTenantLocal(t);
        setCreditStatus(cs);
        setBizName(t?.name || "");
        setBizType(t?.type || "");
        setBizAddress(t?.address || "");
        setBizHours(t?.opening_hours || "");
        setBizTimezone(t?.timezone || "");
        setBizPersona(t?.bot_persona || "");
        setBizWelcome(t?.welcome_message || "");
      } catch (err) {
        Sentry.captureException(err, {
          tags: { action: "load_settings_data" },
          extra: { tenantId },
        });
      }
    }
    load();
  }, [tenantId, canAccessBilling, setCreditStatus]);

  useEffect(() => {
    async function loadUsers() {
      if (!orgId) {
        setUsers([]);
        return;
      }
      try {
        const data = await getManagerUsers(orgId);
        setUsers(data);
      } catch (err) {
        setUsers([]);
        Sentry.captureException(err, {
          tags: { action: "load_settings_users" },
          extra: { orgId },
        });
      }
    }
    loadUsers();
  }, [orgId]);

  useEffect(() => {
    setProfileName(authUser?.name || authUser?.username || "");
    setProfilePhone(authUser?.phone || "");
  }, [authUser]);

  const displayRole = normalizeRole(storeRole);
  const name = profileName || authUser?.name || authUser?.username || authUser?.email || tenant?.name || "—";
  const email = authUser?.email ?? "—";

  const creditCycleEnd = formatBillingDate(billingView(creditStatus)?.cycleEnd, i18n.language);
  const billingCycleRight = creditCycleEnd
    ? t("settingsPage.resetsWithDate", { date: creditCycleEnd })
    : "›";

  async function handleLogout() {
    await logoutAuth();
    navigate(`${prefix}/login`);
  }

  async function handleSaveProfile() {
    setProfileSaving(true);
    setProfileSaveError("");
    setProfileSaveSuccess("");
    try {
      await updateProfile({ name: profileName, phone: profilePhone });
      setAuthUser({ ...useAuthStore.getState().user, name: profileName, phone: profilePhone });
      setProfileSaveSuccess(t("common.saved"));
      setTimeout(() => setProfileSaveSuccess(""), 2000);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: "save_profile" },
        extra: { tenantId },
      });
      setProfileSaveError(t("settingsPage.profileSaveFailed"));
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePickAvatar(event) {
    const file = event.target.files?.[0];
    event.target.value = ""; // picking the same file again must fire onChange
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setAvatarError(t("settingsPage.photoTypeError"));
      return;
    }
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const updated = await uploadAvatar(await resizeImage(file));
      setAuthUser({ ...useAuthStore.getState().user, avatar_url: updated.avatar_url ?? null });
    } catch (err) {
      Sentry.captureException(err, { tags: { action: "upload_avatar" } });
      setAvatarError(err?.response?.data?.detail || t("settingsPage.photoError"));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleRemoveAvatar() {
    setAvatarBusy(true);
    setAvatarError("");
    try {
      await deleteAvatar();
      setAuthUser({ ...useAuthStore.getState().user, avatar_url: null });
    } catch (err) {
      Sentry.captureException(err, { tags: { action: "remove_avatar" } });
      setAvatarError(t("settingsPage.photoError"));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleChangePassword() {
    setPwError("");
    setPwSuccess("");
    if (newPassword !== confirmPassword) {
      setPwError(t("settingsPage.passwordMismatch"));
      return;
    }
    if (newPassword.length < 8) {
      setPwError(t("settingsPage.passwordShort"));
      return;
    }
    setPasswordSaving(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPwSuccess(t("settingsPage.passwordUpdated"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const status = err?.response?.status;
      if (!status || status >= 500) {
        Sentry.captureException(err, {
          tags: { action: "change_password" },
          extra: { tenantId },
        });
      }
      setPwError(err?.response?.data?.detail || t("settingsPage.passwordFailed"));
    } finally {
      setPasswordSaving(false);
    }
  }

  async function handleSaveBusiness() {
    if (!tenantId) return;
    setBusinessSaving(true);
    setBusinessSaveError("");
    setBusinessSaveSuccess("");
    try {
      const body = Object.fromEntries(
        Object.entries({
          name: bizName,
          type: bizType,
          address: bizAddress,
          opening_hours: bizHours,
          timezone: bizTimezone,
          bot_persona: bizPersona,
          welcome_message: bizWelcome,
        }).filter(([, v]) => v !== ""),
      );
      await updateBusiness(tenantId, body);
      setBusinessSaveSuccess(t("common.saved"));
      api
        .post(`/admin/kb/retrain/${tenantId}?bot_type=chatizi`)
        .catch(() => {});
      setTimeout(() => setBusinessSaveSuccess(""), 2000);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: "save_business" },
        extra: { tenantId },
      });
      setBusinessSaveError(t("settingsPage.businessSaveFailed"));
    } finally {
      setBusinessSaving(false);
    }
  }

  const tabs = [
    { id: "account", label: t("settingsPage.tabAccount") },
    { id: "business", label: t("settingsPage.tabBusiness") },
  ];

  return (
    <ResponsiveShell title={t("settingsPage.title")}>
      <div className="mx-auto w-full max-w-5xl py-4 lg:py-6">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-text-main">{t("settingsPage.title")}</h1>
          <p className="text-sm text-text-muted">{t("settingsPage.subtitle")}</p>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <div role="tablist" aria-label={t("settingsPage.title")} className="flex gap-2 lg:w-48 lg:shrink-0 lg:flex-col">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors lg:flex-none ${
                  activeTab === tab.id ? "bg-primary text-white" : "text-text-muted hover:bg-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4">
        {/* ── Account tab ── */}
        {activeTab === "account" && (
          <>
            <Card title={t("settingsPage.personalInfo")}>
              <div className="flex flex-wrap items-center gap-4 border-b border-border pb-5">
                <UserAvatar url={authUser?.avatar_url} name={name} size={64} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text-main">{name}</p>
                  <p className="text-xs font-medium text-primary">
                    {t(`settingsPage.role.${displayRole}`, { defaultValue: displayRole })}
                  </p>
                  <p className="mt-0.5 text-xs text-text-secondary">{t("settingsPage.photoHint")}</p>
                  {avatarError && <p role="alert" className="mt-1 text-xs text-red-500">{avatarError}</p>}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="avatar-input"
                  onChange={handlePickAvatar}
                />
                <div className="flex items-center gap-2">
                  {authUser?.avatar_url && (
                    <button type="button" disabled={avatarBusy} onClick={handleRemoveAvatar} className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-muted hover:bg-page-bg disabled:opacity-50">
                      {t("settingsPage.removePhoto")}
                    </button>
                  )}
                  <button type="button" disabled={avatarBusy} onClick={() => fileInputRef.current?.click()} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-text-main hover:bg-page-bg disabled:opacity-50">
                    {avatarBusy ? t("settingsPage.uploading") : t("settingsPage.changePhoto")}
                  </button>
                </div>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <InputField label={t("settingsPage.name")} value={profileName} onChange={(e) => setProfileName(e.target.value)} disabled={profileSaving} />
                <InputField label={t("settingsPage.phone")} value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)} placeholder="+84..." disabled={profileSaving} />
                <InputField label={t("settingsPage.email")} value={email} onChange={() => {}} disabled />
                <InputField label={t("settingsPage.roleLabel")} value={t(`settingsPage.role.${displayRole}`, { defaultValue: displayRole })} onChange={() => {}} disabled />
              </div>
              <SaveRow message={profileSaveSuccess} error={profileSaveError} busy={profileSaving} onSave={handleSaveProfile} label={t("settingsPage.saveChanges")} busyLabel={t("settingsPage.saving")} />
            </Card>

            <Card title={t("settingsPage.changePassword")}>
              <div className="grid max-w-md gap-4">
                <InputField label={t("settingsPage.currentPassword")} type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={passwordSaving} placeholder={t("settingsPage.passwordHint")} autoComplete="current-password" />
                <InputField label={t("settingsPage.newPassword")} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t("settingsPage.passwordHint")} disabled={passwordSaving} autoComplete="new-password" />
                <InputField label={t("settingsPage.confirmPassword")} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t("settingsPage.passwordHint")} disabled={passwordSaving} autoComplete="new-password" />
              </div>
              <SaveRow message={pwSuccess} error={pwError} busy={passwordSaving} onSave={handleChangePassword} label={t("settingsPage.updatePassword")} busyLabel={t("settingsPage.saving")} />
            </Card>

            {/* Section 4 — Team Members (existing) */}
            {users.length > 0 && (
              <div className="card p-0 mb-3">
                <div className="px-4 pt-3 pb-2">
                  <p className="text-text-main font-bold text-sm">
                    {t("settingsPage.teamMembers")}
                  </p>
                </div>
                <div className="divide-y divide-gray-50">
                  {users.map((u) => {
                    const memberRole = normalizeRole(u.role);
                    const rs =
                      ROLE_STYLE[memberRole] ?? ROLE_STYLE.receptionist;
                    const ss =
                      USER_STATUS_STYLE[u.status] ?? USER_STATUS_STYLE.active;
                    return (
                      <div
                        key={u.id ?? u.email}
                        className="px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-[#e3eaf8] flex items-center justify-center text-[#1535c8] text-xs font-bold flex-shrink-0">
                            {(u.name || u.email || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {u.name || u.email}
                            </p>
                            {u.email && u.name && (
                              <p className="text-xs text-gray-400 truncate">
                                {u.email}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`pill ${rs.bg} ${rs.text}`}>
                            {t(`settingsPage.role.${memberRole}`, {
                              defaultValue: memberRole,
                            })}
                          </span>
                          <span className={`pill ${ss.bg} ${ss.text}`}>
                            {t(ss.labelKey)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section 4b — Push notifications */}
            <div className="card p-0 mb-3">
              <div className="px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-text-hint">{BellIcon}</span>
                  <span className="text-text-main text-sm font-medium">{t('settingsPage.pushNotifications')}</span>
                </div>
                <button
                  type="button"
                  onClick={handleTogglePush}
                  disabled={pushBusy}
                  aria-pressed={pushEnabled}
                  className={`relative w-11 h-6 rounded-full transition-colors ${pushEnabled ? 'bg-[#1535c8]' : 'bg-gray-300'} disabled:opacity-50`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${pushEnabled ? 'translate-x-5' : ''}`}
                  />
                </button>
              </div>
            </div>

            {/* Section 5 — App info rows (existing) */}
            <div className="card p-0 mb-3 divide-y divide-gray-50">
              <SettingRow
                icon={GlobeIcon}
                label={t("settingsPage.language")}
                right={`${i18n.language === "vi" ? t("language.vi") : t("language.en")} ›`}
              />
              <SettingRow
                icon={InfoIcon}
                label={t("settingsPage.version")}
                right={`${APP_VERSION}`}
              />
              {canAccessBilling && (
                <SettingRow
                  icon={BillingIcon}
                  label={t("settingsPage.billingCycle")}
                  right={billingCycleRight}
                />
              )}
            </div>

            {/* Section 6 — Logout (existing) */}
            <div className="card p-4 mb-3">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 text-red-500 font-bold text-sm"
              >
                {LogoutIcon}
                {t("settingsPage.logOut")}
              </button>
            </div>
          </>
        )}

        {/* ── Business tab ── */}
        {activeTab === "business" && (
          <>
            <Card title={t("settingsPage.businessInfo")}>
              <div className="grid gap-4 sm:grid-cols-2">
                <InputField label={t("settingsPage.businessName")} value={bizName} onChange={(e) => setBizName(e.target.value)} disabled={businessSaving} />
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-text-muted">{t("settingsPage.businessType")}</span>
                  <select className={FIELD_CLASS} value={bizType} onChange={(e) => setBizType(e.target.value)} disabled={businessSaving}>
                    <option value="">{t("settingsPage.selectType")}</option>
                    {BUSINESS_TYPES.map((bt) => (
                      <option key={bt.value} value={bt.value}>{bt.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </Card>

            <Card title={t("settingsPage.contactHours")}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <InputField label={t("settingsPage.address")} value={bizAddress} onChange={(e) => setBizAddress(e.target.value)} disabled={businessSaving} />
                </div>
                <InputField label={t("settingsPage.openingHours")} value={bizHours} onChange={(e) => setBizHours(e.target.value)} placeholder="9:00 AM - 10:00 PM" disabled={businessSaving} />
                <InputField label={t("settingsPage.timezone")} value={bizTimezone} onChange={(e) => setBizTimezone(e.target.value)} placeholder="Asia/Ho_Chi_Minh" disabled={businessSaving} />
              </div>
            </Card>

            <Card title={t("settingsPage.botSection")}>
              <div className="grid gap-4">
                <TextareaField label={t("settingsPage.botPersona")} value={bizPersona} onChange={(e) => setBizPersona(e.target.value)} placeholder={t("settingsPage.botPersonaHint")} rows={3} disabled={businessSaving} />
                <TextareaField label={t("settingsPage.welcomeMessage")} value={bizWelcome} onChange={(e) => setBizWelcome(e.target.value)} placeholder={t("settingsPage.welcomeMessageHint")} rows={3} disabled={businessSaving} />
              </div>
              <SaveRow message={businessSaveSuccess} error={businessSaveError} busy={businessSaving} onSave={handleSaveBusiness} label={t("settingsPage.saveChanges")} busyLabel={t("settingsPage.saving")} />
            </Card>
          </>
        )}
          </div>
        </div>
      </div>
    </ResponsiveShell>
  );
}
